!(function () {
  "use strict";

  const SELECTOR_ZOOM_CAPTION = "[data-zoom-caption]";
  const SELECTOR_ZOOM_IMAGE = "[data-zoom-image]";
  const SELECTOR_SECTION_TYPE = "[data-section-type]";
  const SELECTOR_THUMBS_CONTAINER = ".pswp__thumbs";
  const SELECTOR_COLOR_INPUT = "[data-colour-image-selector]";

  const CLASS_DRAGGING = "is-dragging";
  const CLASS_SOLDOUT = "variant--soldout";
  const CLASS_UNAVAILABLE = "variant--unavailable";
  const CLASS_ZOOM_GALLERY = "pswp-zoom-gallery";
  const CLASS_ZOOM_GALLERY_SINGLE = "pswp-zoom-gallery--single";

  const ATTR_IMAGE_SRC = "data-image-src";
  const ATTR_IMAGE_WIDTH = "data-image-width";
  const ATTR_IMAGE_HEIGHT = "data-image-height";

  class WallpaperZoomImages extends HTMLElement {
    constructor() {
      super();
      this.container = this.closest(SELECTOR_SECTION_TYPE);
      this.colorInputs = document.querySelectorAll(SELECTOR_COLOR_INPUT);
      this.currentColorData = null;
      this.allColorData = null;
      this.zoomCaptions = null;
      this.thumbsContainer = document.querySelector(SELECTOR_THUMBS_CONTAINER);
      this.dimensionsCache = new Map(); // Cache dimensions cho các URL
    }

    connectedCallback() {
      this.zoomCaptions = this.container?.querySelector(SELECTOR_ZOOM_CAPTION);
      this.loadColorData();
      this.attachZoomListeners();
      this.observeColorChange();
    }

    /**
     * Lấy dữ liệu màu từ metadata JSON
     */
    loadColorData() {
      const jsonEl = document.getElementById("color-images-json");
      if (jsonEl) {
        try {
          this.allColorData = JSON.parse(jsonEl.textContent);
        } catch (e) {
          console.error("Parse color data failed:", e);
        }
      }
    }

    /**
     * Parse dimensions từ URL Shopify
     * Format thường: filename_{width}x{height}.jpg hoặc filename_{size}.jpg
     * @param {string} url - URL của hình ảnh
     * @returns {Object|null} Object chứa width và height, hoặc null nếu không parse được
     */
    parseDimensionsFromUrl(url) {
      if (!url) return null;

      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;

        // Pattern 1: _{width}x{height}. (ví dụ: _800x1200.jpg)
        const dimensionsPattern = /_(\d+)x(\d+)\./;
        const match = pathname.match(dimensionsPattern);

        if (match) {
          return {
            width: parseInt(match[1]),
            height: parseInt(match[2]),
          };
        }

        // Pattern 2: _{size}. (ví dụ: _800x800_@2x.jpg hoặc _large.jpg)
        // Trong trường hợp này, cần load ảnh để lấy kích thước thực
        return null;
      } catch (e) {
        console.warn("Parse URL failed:", e);
        return null;
      }
    }

    /**
     * Load ảnh để lấy kích thước thực tế (naturalWidth, naturalHeight)
     * @param {string} url - URL của hình ảnh
     * @returns {Promise<Object>} Promise trả về {width, height}
     */
    async getImageDimensions(url) {
      // Kiểm tra cache trước
      if (this.dimensionsCache.has(url)) {
        return this.dimensionsCache.get(url);
      }

      // Thử parse từ URL trước
      const parsed = this.parseDimensionsFromUrl(url);
      if (parsed) {
        this.dimensionsCache.set(url, parsed);
        return parsed;
      }

      // Nếu không parse được, load ảnh để lấy kích thước thực
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const dimensions = {
            width: img.naturalWidth,
            height: img.naturalHeight,
          };
          this.dimensionsCache.set(url, dimensions);
          resolve(dimensions);
        };
        img.onerror = () => {
          // Fallback dimensions nếu load lỗi
          const fallback = { width: 1200, height: 1200 };
          this.dimensionsCache.set(url, fallback);
          resolve(fallback);
        };
        img.src = url;
      });
    }

    /**
     * Lấy 3 ảnh của màu hiện tại kèm kích thước (async version)
     * @returns {Promise<Object|null>} Promise trả về Object chứa variant, room, hover với url và dimensions
     */
    async getCurrentColorImages() {
      const checkedInput = document.querySelector(
        `${SELECTOR_COLOR_INPUT}:checked`
      );
      if (!checkedInput) return null;

      const variantImage = checkedInput.dataset.variantImage;
      const colorName = checkedInput.value.toLowerCase().trim();
      const colorData = this.allColorData?.find(
        (c) => c.color.toLowerCase().trim() === colorName
      );

      const roomImage = colorData?.images.find((i) => i.type === "room")?.url;
      const hoverImage = colorData?.images.find((i) => i.type === "hover")?.url;

      // Lấy kích thước thực tế từ URL (async)
      const [variantDims, roomDims, hoverDims] = await Promise.all([
        this.getImageDimensions(variantImage),
        roomImage ? this.getImageDimensions(roomImage) : Promise.resolve(null),
        hoverImage ? this.getImageDimensions(hoverImage) : Promise.resolve(null),
      ]);

      return {
        variant: {
          url: variantImage,
          width: variantDims?.width,
          height: variantDims?.height,
        },
        room: {
          url: roomImage,
          width: roomDims?.width,
          height: roomDims?.height,
        },
        hover: {
          url: hoverImage,
          width: hoverDims?.width,
          height: hoverDims?.height,
        },
      };
    }

    /**
     * Gắn sự kiện zoom cho 3 ảnh sản phẩm
     */
    attachZoomListeners() {
      const images = [
        document.querySelector(".pdp-media-item-1 " + SELECTOR_ZOOM_IMAGE),
        document.querySelector(".pdp-media-item-2 " + SELECTOR_ZOOM_IMAGE),
        document.querySelector(".pdp-media-item-3 " + SELECTOR_ZOOM_IMAGE),
      ];

      images.forEach((img, index) => {
        if (!img) return;

        // Click event
        img.addEventListener("click", (e) => {
          e.preventDefault();
          if (img.closest(`.${CLASS_DRAGGING}`)) return;
          this.openZoom(index);
          window.a11y.lastElement = img;
        });

        // Keyboard accessibility
        img.addEventListener("keyup", (e) => {
          if (e.code === "Enter") {
            e.preventDefault();
            this.openZoom(index);
            window.a11y.lastElement = img;
          }
        });
      });
    }

    /**
     * Mở zoom gallery với PhotoSwipe (async version)
     * @param {number} startIndex - Index của ảnh được click (0-2)
     */
    async openZoom(startIndex) {
      const colorImages = await this.getCurrentColorImages();
      if (!colorImages) return;

      const items = [
        this.createPhotoswipeItem(colorImages.variant),
        this.createPhotoswipeItem(colorImages.room),
        this.createPhotoswipeItem(colorImages.hover),
      ].filter((item) => item !== null);

      if (items.length === 0) return;

      const options = {
        history: false,
        focus: false,
        index: startIndex,
        mainClass:
          items.length === 1
            ? `${CLASS_ZOOM_GALLERY} ${CLASS_ZOOM_GALLERY_SINGLE}`
            : CLASS_ZOOM_GALLERY,
        showHideOpacity: true,
        howAnimationDuration: 150,
        hideAnimationDuration: 250,
        closeOnScroll: false,
        closeOnVerticalDrag: false,
        captionEl: true,
        closeEl: true,
        closeElClasses: ["caption-close", "title"],
        tapToClose: false,
        clickToCloseNonZoomable: false,
        maxSpreadZoom: 2,
        loop: true,
        spacing: 0,
        allowPanToNext: true,
        pinchToClose: false,
        addCaptionHTMLFn: (item, captionEl, isFake) => {
          return this.addCaption(item, captionEl, isFake);
        },
        getThumbBoundsFn: (index) => {
          return this.getThumbBounds(index);
        },
      };

      new window.theme.LoadPhotoswipe(items, options);
      this.updateThumbnails(colorImages);
    }

    /**
     * Tạo item cho PhotoSwipe với kích thước động
     * @param {Object} imageData - Object chứa url, width, height
     * @returns {Object|null} PhotoSwipe item object
     */
    createPhotoswipeItem(imageData) {
      if (!imageData || !imageData.url) return null;

      // Sử dụng kích thước thực tế đã lấy từ URL/Img
      // width và height giờ đã được tính toán chính xác từ getImageDimensions()
      const width = imageData.width ? parseInt(imageData.width) : 1200;
      const height = imageData.height ? parseInt(imageData.height) : 1200;

      return {
        src: imageData.url,
        w: width,
        h: height,
        msrc: imageData.url,
      };
    }

    /**
     * Cập nhật thumbnails trong popup PhotoSwipe
     * @param {Object} colorImages - Object chứa variant, room, hover data
     */
    updateThumbnails(colorImages) {
      if (!this.thumbsContainer) return;

      const thumbsHTML = [
        colorImages.variant.url,
        colorImages.room.url,
        colorImages.hover.url,
      ]
        .filter((url) => url)
        .map((url) => `<img src="${url}" class="pswp__thumb" alt="">`)
        .join("");

      this.thumbsContainer.innerHTML = thumbsHTML;
    }

    /**
     * Thêm caption vào PhotoSwipe (kế thừa từ zoom.js)
     * @param {Object} item - PhotoSwipe item
     * @param {HTMLElement} captionEl - Caption element
     * @param {boolean} isFake - Is fake caption
     * @returns {boolean}
     */
    addCaption(item, captionEl, isFake) {
      if (!this.zoomCaptions) return false;

      const captionInner = captionEl.children[0];
      let captionHTML = this.zoomCaptions.innerHTML;

      // Xử lý class soldout/unavailable
      const isSoldout = this.zoomCaptions.closest(`.${CLASS_SOLDOUT}`);
      const isUnavailable = this.zoomCaptions.closest(`.${CLASS_UNAVAILABLE}`);

      if (isSoldout) {
        captionInner.classList.add(CLASS_SOLDOUT);
      } else {
        captionInner.classList.remove(CLASS_SOLDOUT);
      }

      if (isUnavailable) {
        captionInner.classList.add(CLASS_UNAVAILABLE);
      } else {
        captionInner.classList.remove(CLASS_UNAVAILABLE);
      }

      captionInner.innerHTML = captionHTML;
      return false;
    }

    /**
     * Lấy vị trí thumbnail để animate (kế thừa từ zoom.js)
     * @param {number} index - Index của ảnh
     * @returns {Object} Thumbnail bounds
     */
    getThumbBounds(index) {
      const images = [
        document.querySelector(".pdp-media-item-1 " + SELECTOR_ZOOM_IMAGE),
        document.querySelector(".pdp-media-item-2 " + SELECTOR_ZOOM_IMAGE),
        document.querySelector(".pdp-media-item-3 " + SELECTOR_ZOOM_IMAGE),
      ];

      const img = images[index];
      if (!img) return { x: 0, y: 0, w: 0 };

      const pageYScroll = window.scrollY || document.documentElement.scrollTop;
      const rect = img.getBoundingClientRect();

      return {
        x: rect.left,
        y: rect.top + pageYScroll,
        w: rect.width,
      };
    }

    /**
     * Theo dõi thay đổi màu và cập nhật attributes với debounce
     */
    observeColorChange() {
      let debounceTimer;

      this.colorInputs.forEach((input) => {
        input.addEventListener("change", async () => {
          // Clear previous timer
          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }

          // Debounce để tránh load quá nhiều lần khi đổi màu nhanh
          debounceTimer = setTimeout(async () => {
            try {
              await this.updateZoomImageAttributes();
            } catch (error) {
              console.error("Failed to update zoom image attributes:", error);
            }
          }, 100); // 100ms debounce
        });
      });
    }

    /**
     * Cập nhật data attributes khi đổi màu (async version)
     */
    async updateZoomImageAttributes() {
      const colorImages = await this.getCurrentColorImages();
      if (!colorImages) return;

      const images = [
        document.querySelector(".pdp-media-item-1 " + SELECTOR_ZOOM_IMAGE),
        document.querySelector(".pdp-media-item-2 " + SELECTOR_ZOOM_IMAGE),
        document.querySelector(".pdp-media-item-3 " + SELECTOR_ZOOM_IMAGE),
      ];

      const imageData = [
        colorImages.variant,
        colorImages.room,
        colorImages.hover,
      ];

      images.forEach((img, i) => {
        if (img && imageData[i]?.url) {
          img.setAttribute(ATTR_IMAGE_SRC, imageData[i].url);

          if (imageData[i].width) {
            img.setAttribute(ATTR_IMAGE_WIDTH, imageData[i].width);
          }

          if (imageData[i].height) {
            img.setAttribute(ATTR_IMAGE_HEIGHT, imageData[i].height);
          }
        }
      });
    }
  }

  // Đăng ký custom element
  if (!customElements.get("zoom-images")) {
    customElements.define("zoom-images", WallpaperZoomImages);
  }
})();
